import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Check, AlertCircle } from "lucide-react";
import { signInSchema, signUpSchema, sanitizeText, type SignInData, type SignUpData } from "@/lib/validations";
import { EmailVerificationDialog } from "@/components/EmailVerificationDialog";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type DisplayNameSuggestion = { name: string; available: boolean };

const AuthModal = ({ isOpen, onClose }: AuthModalProps) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const [turnstileWidgetId, setTurnstileWidgetId] = useState<string | null>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const [suggestions, setSuggestions] = useState<DisplayNameSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionTimestamps, setSuggestionTimestamps] = useState<number[]>([]);
  const [inspirationText, setInspirationText] = useState("");
  const [usedAISuggestion, setUsedAISuggestion] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState("");

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const isEmailVerification = hashParams.get("access_token") || hashParams.get("type") === "recovery";

    if (isEmailVerification && isOpen) {
      toast.success("Email verified successfully!");
    }

    if (!isOpen) {
      setEmail("");
      setPassword("");
      setDisplayName("");
      setIsLogin(true);
      setErrors({});
      setTurnstileToken("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isLogin && isOpen && turnstileRef.current) {
      const renderWidget = () => {
        if (window.turnstile && turnstileRef.current && !turnstileWidgetId) {
          const widgetId = window.turnstile.render(turnstileRef.current, {
            sitekey: "0x4AAAAAAB6GJ-kn8z1tVMrZ",
            callback: (token: string) => {
              setTurnstileToken(token);
            },
            "error-callback": () => {
              toast.error("CAPTCHA verification failed. Please try again.");
              setTurnstileToken("");
            },
            theme: "light",
          });
          setTurnstileWidgetId(widgetId);
        }
      };

      if (window.turnstile) {
        renderWidget();
      } else {
        const checkInterval = setInterval(() => {
          if (window.turnstile) {
            renderWidget();
            clearInterval(checkInterval);
          }
        }, 100);

        return () => clearInterval(checkInterval);
      }
    }

    return () => {
      if (turnstileWidgetId && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId);
        setTurnstileWidgetId(null);
      }
    };
  }, [isLogin, isOpen, turnstileWidgetId]);

  const handleGetSuggestions = async () => {
    if (!displayName.trim()) {
      toast.error("Please enter inspiration keywords in the Display Name field");
      return;
    }

    const words = displayName.trim().split(/\s+/);
    if (words.length < 5) {
      toast.error("Please enter at least 5 words (e.g., colors, activities, interests)");
      return;
    }

    const sessionKey = localStorage.getItem("session_key");
    if (!sessionKey) {
      toast.error("Session not initialized. Please refresh the page.");
      return;
    }

    // Save the inspiration text before making the call
    setInspirationText(displayName.trim());

    setLoadingSuggestions(true);
    setSuggestions([]);

    try {
      const { data, error } = await supabase.functions.invoke("suggest-display-name", {
        body: {
          inspiration: displayName.trim(),
          session_key: sessionKey,
        },
      });

      // Handle specific error codes from edge function
      if (error || data?.error) {
        // When edge function returns non-2xx, error object contains the response
        let errorCode = "UNKNOWN_ERROR";
        let message = undefined;
        let remainingSeconds = undefined;

        if (error) {
          // Try to parse error context (for FunctionsHttpError)
          try {
            const errorContext = (error as any)?.context;
            if (errorContext) {
              errorCode = errorContext.error || "UNKNOWN_ERROR";
              message = errorContext.message;
              remainingSeconds = errorContext.remainingSeconds;
            }
          } catch (e) {
            console.error("Error parsing error context:", e);
          }
        } else if (data?.error) {
          errorCode = data.error;
          message = data.message;
          remainingSeconds = data.remainingSeconds;
        }

        handleAIError(errorCode, message, remainingSeconds);
        return;
      }

      if (data?.suggestions && data.suggestions.length > 0) {
        setSuggestions(data.suggestions);
        toast.success("Here are some suggestions!");
      } else {
        toast.error("No suggestions generated. Try different keywords.");
      }
    } catch (error) {
      console.error("Error calling suggest-display-name:", error);
      toast.error("Failed to get suggestions. Please try again.");
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleAIError = (errorCode: string, message?: string, remainingSeconds?: number) => {
    const errorMessages: Record<string, string> = {
      FEATURE_DISABLED: "AI suggestions are temporarily unavailable. Please choose a display name manually.",
      DAILY_LIMIT_REACHED: "Daily AI suggestion limit reached. Please try again tomorrow or choose manually.",
      SESSION_LIMIT_REACHED:
        "You've reached the maximum AI suggestions for this session. Please choose a display name manually.",
      RATE_LIMIT: message || "Please wait before requesting more suggestions.",
      CREDITS_EXHAUSTED:
        "AI suggestions are temporarily unavailable due to high demand. Please choose a display name manually.",
      SESSION_NOT_FOUND: "Session not initialized. Please refresh the page.",
      INVALID_SESSION: "Invalid session. Please refresh the page.",
      LOVABLE_RATE_LIMIT: "AI service is busy. Please try again in a moment.",
      INSPIRATION_REQUIRED: "Please enter inspiration keywords.",
      INSUFFICIENT_WORDS: "Please provide at least 5 words for inspiration.",
      AI_NOT_CONFIGURED: "AI service is not configured.",
      UNKNOWN_ERROR: "Failed to get suggestions. Please try again.",
    };

    const displayMessage = errorMessages[errorCode] || message || errorMessages["UNKNOWN_ERROR"];

    // For rate limits with countdown, show the remaining time
    if (errorCode === "RATE_LIMIT" && remainingSeconds) {
      toast.error(`Please wait ${remainingSeconds} seconds before requesting more suggestions.`);
    } else {
      toast.error(displayMessage);
    }

    console.error("AI Error:", errorCode, message);
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const formData = {
        email: sanitizeText(email),
        password,
        ...(isLogin ? {} : { displayName: sanitizeText(displayName) }),
      };

      const schema = isLogin ? signInSchema : signUpSchema;
      const validation = schema.safeParse(formData);

      if (!validation.success) {
        const fieldErrors: Record<string, string> = {};
        validation.error.issues.forEach((error) => {
          if (error.path[0]) {
            fieldErrors[error.path[0] as string] = error.message;
          }
        });
        setErrors(fieldErrors);
        return;
      }

      if (isLogin) {
        const { data: signInData, error } = await supabase.auth.signInWithPassword({
          email: validation.data.email,
          password: validation.data.password,
        });
        if (error) throw error;

        // Check if email is verified
        if (signInData.user && !signInData.user.email_confirmed_at) {
          console.warn("⚠️ User attempted login with unverified email");

          // Sign them out immediately
          await supabase.auth.signOut();

          // Show verification dialog
          setUnverifiedEmail(validation.data.email);
          setShowVerificationDialog(true);
          return;
        }

        toast.success("Signed in successfully!");
        onClose();
      } else {
        if (!turnstileToken) {
          toast.error("Please complete the CAPTCHA verification");
          return;
        }

        const { data: verifyData, error: verifyError } = await supabase.functions.invoke("verify-turnstile", {
          body: { token: turnstileToken },
        });

        if (verifyError || !verifyData?.success) {
          toast.error("CAPTCHA verification failed. Please try again.");
          setTurnstileToken("");
          if (window.turnstile) {
            window.turnstile.reset();
          }
          return;
        }

        // 1. Check if user already exists BEFORE signup (edge function sends "attempt detected" email if they exist)
        const { data: existingUserCheck } = await supabase.functions.invoke("send-signup-notification", {
          body: {
            email: validation.data.email,
            checkOnly: true,
          },
        });

        // 2. If user exists, edge function already sent "attempt detected" email - just show UI message and stop
        if (existingUserCheck?.userExists) {
          toast.success(
            "Account created! Please check your email and click the verification link to activate your account.",
          );
          onClose();
          return;
        }

        // 3. Otherwise, proceed with normal signup
        const redirectUrl = `${window.location.origin}${window.location.pathname}`;
        const displayNameValue = (validation.data as SignUpData).displayName || "";

        const { data: signUpData, error } = await supabase.auth.signUp({
          email: validation.data.email,
          password: validation.data.password,
          options: {
            emailRedirectTo: redirectUrl,
            data: {
              display_name: displayNameValue,
              inspiration_text: usedAISuggestion && inspirationText ? inspirationText : null,
            },
          },
        });

        if (error) throw error;

        // Supabase automatically sends the verification email
        // A welcome email with avatar will be sent after verification via post-verification-setup trigger
        toast.success(
          "Account created! Please check your email and click the verification link to activate your account.",
        );
        onClose();
      }
    } catch (error: any) {
      const errorMessage = error.message?.toLowerCase() || "";

      // Check for email not confirmed errors
      if (errorMessage.includes("email not confirmed") || errorMessage.includes("email_not_confirmed")) {
        setUnverifiedEmail(email);
        setShowVerificationDialog(true);
        return;
      }

      // Check for rate limit errors
      if (errorMessage.includes("rate limit") || errorMessage.includes("email rate limit")) {
        toast.error(
          "Too many signup attempts. Please try again in about an hour, or try using a different email address.",
        );
      } else {
        toast.error(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async () => {
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: sanitizeText(email),
        options: {
          emailRedirectTo: `${window.location.origin}${window.location.pathname}`,
        },
      });

      if (error) throw error;
      toast.success("Check your email for the magic link! Clicking it will verify your email and sign you in.");
      onClose();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <CardHeader className="pb-2 pt-4">
          <CardTitle>{isLogin ? "Sign In" : "Sign Up"}</CardTitle>
          {isLogin && <CardDescription>Welcome back!</CardDescription>}
        </CardHeader>

        <CardContent className="pt-4">
          <form onSubmit={handleAuth} className="space-y-3">
            {!isLogin && (
              <div className="space-y-1.5">
                <Label htmlFor="displayName">
                  Display Name
                  <span className="block text-xs font-normal text-muted-foreground mt-0.5">
                    or just type some words of inspiration and let the bot help
                  </span>
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="displayName"
                    type="text"
                    placeholder="blue gymnastics soccer dance transport"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    maxLength={200}
                    required
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleGetSuggestions}
                    disabled={!displayName.trim() || loadingSuggestions}
                    className="shrink-0"
                  >
                    {loadingSuggestions ? <Loader2 className="h-4 w-4 animate-spin" /> : "✨ AI"}
                  </Button>
                </div>
                {errors.displayName && (
                  <p className="text-sm text-destructive">
                    {errors.displayName}
                    {(errors.displayName.includes("characters") || errors.displayName.includes("contain")) &&
                      " Try AI suggestions."}
                  </p>
                )}

                {suggestions.length > 0 && (
                  <div className="space-y-0.5 border rounded-md p-1.5 bg-muted/50">
                    <p className="text-xs text-muted-foreground px-2 py-1">Click a suggestion to use it:</p>
                    {suggestions.map((suggestion, i) => (
                      <button
                        key={i}
                        type="button"
                        className="flex items-center justify-between w-full text-left px-3 py-2 rounded hover:bg-accent hover:text-accent-foreground transition-colors"
                        onClick={() => {
                          setDisplayName(suggestion.name);
                          setUsedAISuggestion(true);
                          setSuggestions([]);
                          setErrors((prev) => ({ ...prev, displayName: "" }));
                          toast.success(`Display name set to: ${suggestion.name}`);
                        }}
                      >
                        <span className="font-medium">{suggestion.name}</span>
                        {suggestion.available ? (
                          <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                            <Check className="h-3 w-3" />
                            Available
                          </span>
                        ) : (
                          <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            Taken
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
              {!isLogin && (
                <p className="text-xs text-muted-foreground">
                  Password must be at least 8 characters with uppercase, lowercase, number, and special character.
                </p>
              )}
            </div>

            {!isLogin && (
              <div className="flex justify-center">
                <div ref={turnstileRef}></div>
              </div>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Loading..." : isLogin ? "Sign In" : "Sign Up"}
            </Button>
          </form>

          {isLogin && (
            <>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleMagicLink}
                disabled={loading || !email}
              >
                Send Magic Link
              </Button>
            </>
          )}

          <div className="mt-2 text-center">
            <Button variant="ghost" onClick={() => setIsLogin(!isLogin)} className="text-sm h-8">
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </Button>
          </div>
        </CardContent>
      </DialogContent>

      <EmailVerificationDialog
        isOpen={showVerificationDialog}
        onClose={() => setShowVerificationDialog(false)}
        email={unverifiedEmail}
      />
    </Dialog>
  );
};

export default AuthModal;
